/**
 * @file src/routes/segments/adminRoutes.tsx
 *
 * Admin route cluster — extracted from `src/routes/index.tsx` per
 * architecture audit #10. Owns every route that mounts under `/admin`
 * plus the standalone `/admin/backfill-matching` and
 * `/admin/match-telemetry` utilities.
 *
 * Composed into the main <Routes> tree via <>{adminRoutes()}</>.
 */
import { lazy, type ReactNode } from "react";
import { Route } from "react-router";

const AdminBackfillMatching = lazy(() => import("@/features/admin/pages/AdminBackfillMatching"));
const AdminMatchTelemetry = lazy(() => import("@/features/admin/pages/AdminMatchTelemetry"));
const AdminLayout = lazy(() => import("@/features/admin/AdminLayout"));
const AdminOverviewSection = lazy(() => import("@/features/admin/components/OverviewSection"));
const AdminUserManagementSection = lazy(() => import("@/features/admin/components/UserManagementSection"));
const AdminCompanyManagementSection = lazy(() => import("@/features/admin/components/CompanyManagementSection"));
const AdminJobModerationSection = lazy(() => import("@/features/admin/components/JobModerationSection"));
const AdminApplicationsSection = lazy(() => import("@/features/admin/components/ApplicationsSection"));
const AdminLearningManagementSection = lazy(() => import("@/features/admin/components/LearningManagementSection"));
const AdminConfigurationSection = lazy(() => import("@/features/admin/components/ConfigurationSection"));
const AdminAuditLogSection = lazy(() => import("@/features/admin/components/AuditLogSection"));
const AdminAnalyticsSection = lazy(() => import("@/features/admin/components/AnalyticsSection"));
const AdminJobIngestionWidget = lazy(() => import("@/features/admin/components/JobIngestionWidget"));
const AdminSecurityMonitoringSection = lazy(() => import("@/features/admin/components/SecurityMonitoringSection"));
const AdminDatabaseMonitoringSection = lazy(() => import("@/features/admin/components/DatabaseMonitoringSection"));
const AdminBugReportsSection = lazy(() => import("@/features/admin/components/BugReportsSection"));
const AdminCustomSkillReviewSection = lazy(() => import("@/features/admin/components/CustomSkillReviewSection"));
const AdminFeatureMapSection = lazy(() => import("@/features/admin/components/FeatureMapSection"));
const AdminTrainingExportsSection = lazy(() => import("@/features/admin/components/TrainingExportsSection"));
const AdminPromoCodesSection = lazy(() => import("@/features/admin/components/PromoCodesSection"));
const AdminDesignLab = lazy(() => import("@/features/admin/pages/DesignLab"));
const AdminDistributionPage = lazy(() => import("@/features/admin/distribution/pages/DistributionPage"));
const AdminVerificationsSection = lazy(() => import("@/features/admin/components/VerificationsSection"));

interface AdminRoutesOptions {
  withLayout: (element: ReactNode, options?: { hideNavigation?: boolean; hideFooter?: boolean }) => ReactNode;
  withRoleProtection: (element: ReactNode, allowedRoles: Array<"candidate" | "employer" | "admin">) => ReactNode;
}

export const adminRoutes = ({ withLayout, withRoleProtection }: AdminRoutesOptions) => (
  <>
    {/* Admin — nested URL-driven routes wrapped in AdminLayout shell. */}
    <Route
      path="/admin"
      element={withLayout(withRoleProtection(<AdminLayout />, ["admin"]), { hideFooter: true })}
    >
      <Route index element={<AdminOverviewSection />} />
      <Route path="overview" element={<AdminOverviewSection />} />
      <Route path="users/*" element={<AdminUserManagementSection />} />
      <Route path="companies/*" element={<AdminCompanyManagementSection />} />
      <Route path="jobs/*" element={<AdminJobModerationSection />} />
      <Route path="applications" element={<AdminApplicationsSection />} />
      <Route path="ingestion/*" element={<AdminJobIngestionWidget />} />
      <Route path="security/*" element={<AdminSecurityMonitoringSection />} />
      <Route path="database/*" element={<AdminDatabaseMonitoringSection />} />
      <Route path="analytics/*" element={<AdminAnalyticsSection />} />
      <Route path="audit/*" element={<AdminAuditLogSection />} />
      <Route path="learning/*" element={<AdminLearningManagementSection />} />
      <Route path="bugs" element={<AdminBugReportsSection />} />
      <Route path="custom-skills" element={<AdminCustomSkillReviewSection />} />
      <Route path="feature-map" element={<AdminFeatureMapSection />} />
      <Route path="config/*" element={<AdminConfigurationSection />} />
      <Route path="training-exports" element={<AdminTrainingExportsSection />} />
      <Route path="promo-codes" element={<AdminPromoCodesSection />} />
      <Route path="design-lab" element={<AdminDesignLab />} />
      <Route path="distribution" element={<AdminDistributionPage />} />
      <Route path="verifications" element={<AdminVerificationsSection />} />
    </Route>
    <Route
      path="/admin/backfill-matching"
      element={withLayout(withRoleProtection(<AdminBackfillMatching />, ["admin"]), { hideNavigation: true, hideFooter: true })}
    />
    <Route
      path="/admin/match-telemetry"
      element={withLayout(withRoleProtection(<AdminMatchTelemetry />, ["admin"]), { hideFooter: true })}
    />
  </>
);
